import { Component, computed, Signal } from '@angular/core';
import { DataItemTemplate } from '../../../templates/data-item-template/data-item-template';
import { Icon } from '../../icon/icon';
import { SpeedPipe } from '../../../pipes/speed-pipe';
import { UnitType } from '../../../models/unit.model';
import { CounterEffect } from '../../../directives/counter-effect';
import { TrainingModeType } from '../../../models/training-mode.model';

type SpeedTargetStateType = 'correct' | 'up' | 'down';

@Component({
  selector: 'li[app-speed-data-block-item]',
  imports: [Icon, DataItemTemplate, SpeedPipe, CounterEffect],
  template: `
    @let speedUnit = value() | speed: unit() : 'unit';
    @let state = targetState();
    <app-data-item-template [dataBlock]="dataBlock()">
      <app-icon [size]="iconSize()" [class]="'icon ' + (state ?? '')" icon="speed" />
      @if (!isFlip()) {
        <div [class]="'unit ' + size()">
          @if (arrowDirection(); as direction) {
            <p [class]="'arrow ' + direction">▲</p>
          }
          <p class="name">{{ speedUnit }}</p>
        </div>
        <p
          [class]="'value ' + (state ?? '')"
          [appCounterEffect]="value()"
          [transform]="speedTransform"
        ></p>
      } @else {
        <span>
          <p class="name">{{ speedUnit }}</p>
          <p class="average-name">moy.</p>
        </span>
        <p class="value">
          <span class="value" [appCounterEffect]="average()" [transform]="speedTransform"></span>
        </p>
      }
    </app-data-item-template>
  `,
  styleUrl: './speed-data-block-item.scss',
})
export class SpeedDataBlockItem extends DataItemTemplate {
  private readonly RATE_LOWER_BOUND: number = 0.8;
  private readonly RATE_UPPER_BOUND: number = 1.2;

  value: Signal<number> = computed(() => this.broadcastData().speed);
  average: Signal<number> = computed(() => this.broadcastData().avgSpeed);
  unit: Signal<UnitType> = computed(
    () => (localStorage.getItem('unit') as UnitType) || undefined || 'metric',
  );
  rate: Signal<number> = computed(() => this.webSocketService.rate());

  speedPipe: SpeedPipe = new SpeedPipe();
  speedTransform: (value: number) => string = (value: number) =>
    this.speedPipe.transform(value, this.unit(), 'value');

  trainingMode: Signal<TrainingModeType> = computed(
    () => this.webSocketService.contentInformation()?.trainingMode || 'originalSpeed',
  );

  targetState: Signal<SpeedTargetStateType | null> = computed<SpeedTargetStateType | null>(() => {    
    if (this.isFlip() || this.trainingMode() !== 'variableSpeed') return null;
    if (this.rate() < this.RATE_LOWER_BOUND) return 'down';
    if (this.rate() > this.RATE_UPPER_BOUND) return 'up';
    return 'correct';
  });

  arrowDirection: Signal<'up' | 'down' | null> = computed<'up' | 'down' | null>(() => {
    const targetState: SpeedTargetStateType | null = this.targetState();
    return targetState === 'up' || targetState === 'down' ? targetState : null;
  });
}
